import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { periodsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { teacherAssignmentsApi, timetablesApi } from '@/services/api/endpoints/teaching';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { DayOfWeek, PeriodDefinition, Timetable, TimetableEntry } from '@/types/domain';

const DAYS: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

const dayLabel = (day: DayOfWeek) => day.charAt(0) + day.slice(1, 3).toLowerCase();

const STATUS_TONE = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'neutral' } as const;

/**
 * Building and publishing a section's weekly schedule.
 *
 * Rendered as a grid of periods × days because that is the shape of the thing
 * being built, and an empty cell is the affordance for filling it — a list of
 * lessons would leave the gaps invisible, which is exactly what the person
 * building a timetable is looking for.
 *
 * Publishing archives the outgoing timetable rather than editing it, so a
 * published schedule is read-only here by design.
 */
export function TimetablePage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [sectionId, setSectionId] = useState('');
  const [timetableId, setTimetableId] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [slot, setSlot] = useState<{ day: DayOfWeek; period: PeriodDefinition } | null>(null);
  const [assignmentId, setAssignmentId] = useState('');
  const [room, setRoom] = useState('');

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ size: 200 }),
    queryFn: () => sectionsApi.list({ page: 0, size: 200 }),
  });
  const selectedSection = sections.data?.data.find((section) => section.id === sectionId);

  const timetables = useQuery({
    queryKey: queryKeys.timetables.forSection(sectionId),
    queryFn: () => timetablesApi.list(sectionId),
    enabled: sectionId !== '',
  });

  const timetable = useQuery({
    queryKey: queryKeys.timetables.detail(timetableId),
    queryFn: () => timetablesApi.get(timetableId),
    enabled: timetableId !== '',
  });

  const periods = useQuery({
    queryKey: queryKeys.periods.forCampus(selectedSection?.campusId ?? ''),
    queryFn: () => periodsApi.list(selectedSection?.campusId ?? ''),
    enabled: selectedSection?.campusId !== undefined,
  });

  /* Only assignments for this section can be scheduled onto it — the backend
     refuses anything else, and offering them would be a dead end. */
  const assignments = useQuery({
    queryKey: queryKeys.teacherAssignments.list({ sectionId }),
    queryFn: () => teacherAssignmentsApi.list({ sectionId }),
    enabled: slot !== null && sectionId !== '',
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.timetables.all });

  const createTimetable = useMutation({
    mutationFn: () =>
      timetablesApi.create({
        sectionId,
        name: newName === '' ? undefined : newName,
        effectiveFrom,
      }),
    onSuccess: (created) => {
      toast.success('Draft timetable created');
      setCreating(false);
      setTimetableId(created.id);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const addEntry = useMutation({
    mutationFn: () =>
      timetablesApi.addEntry(timetableId, {
        dayOfWeek: slot?.day ?? 'MONDAY',
        periodId: slot?.period.id ?? '',
        teacherAssignmentId: assignmentId,
        room: room === '' ? undefined : room,
      }),
    onSuccess: () => {
      toast.success('Lesson added');
      setSlot(null);
      setAssignmentId('');
      setRoom('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const removeEntry = useMutation({
    mutationFn: (entryId: string) => timetablesApi.removeEntry(timetableId, entryId),
    onSuccess: () => {
      toast.success('Lesson removed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const publish = useMutation({
    mutationFn: () => timetablesApi.publish(timetableId),
    onSuccess: () => {
      toast.success('Timetable published. The previous one has been archived.');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const entryAt = (day: DayOfWeek, periodId: string): TimetableEntry | undefined =>
    timetable.data?.entries.find((entry) => entry.dayOfWeek === day && entry.periodId === periodId);

  const editable = timetable.data?.editable === true;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Timetables"
        description="A schedule is a version, not a fact: publishing a new one archives its predecessor rather than rewriting it."
      />

      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            label="Class"
            value={sectionId}
            onChange={(event) => {
              setSectionId(event.target.value);
              setTimetableId('');
            }}
          >
            <option value="">Select a class…</option>
            {sections.data?.data.map((section) => (
              <option key={section.id} value={section.id}>
                {section.gradeName !== undefined ? `${section.gradeName} · ` : ''}
                {section.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Timetable"
            value={timetableId}
            onChange={(event) => setTimetableId(event.target.value)}
            disabled={sectionId === ''}
          >
            <option value="">Select a timetable…</option>
            {timetables.data?.map((entry: Timetable) => (
              <option key={entry.id} value={entry.id}>
                {entry.name ?? 'Schedule'} · {entry.status.toLowerCase()} · from {entry.effectiveFrom}
              </option>
            ))}
          </SelectField>

          {can(PERMISSIONS.TIMETABLE_CREATE) && sectionId !== '' && (
            <div className="flex items-end">
              <Button variant="secondary" onClick={() => setCreating(true)}>
                New draft
              </Button>
            </div>
          )}
        </div>
      </Card>

      {timetableId === '' ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">
            Choose a timetable, or start a new draft.
          </p>
        </Card>
      ) : timetable.isPending || periods.isPending ? (
        <LoadingState label="Loading timetable" />
      ) : timetable.error !== null ? (
        <ErrorState error={timetable.error} onRetry={() => void timetable.refetch()} />
      ) : timetable.data === undefined ? null : (
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink">
                {timetable.data.name ?? 'Schedule'}
              </h2>
              <Badge tone={STATUS_TONE[timetable.data.status]}>
                {timetable.data.status.toLowerCase()}
              </Badge>
              {!editable && (
                <span className="text-xs text-subtle">
                  Published and archived timetables are read-only.
                </span>
              )}
            </div>

            {can(PERMISSIONS.TIMETABLE_CREATE) && timetable.data.status === 'DRAFT' && (
              <Button
                loading={publish.isPending}
                disabled={timetable.data.entries.length === 0}
                onClick={() => publish.mutate()}
              >
                Publish
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr>
                  <th className="w-40 border-b border-line px-2 py-2 text-left text-xs font-medium text-subtle">
                    Period
                  </th>
                  {DAYS.map((day) => (
                    <th
                      key={day}
                      className="border-b border-line px-2 py-2 text-left text-xs font-medium text-subtle"
                    >
                      {dayLabel(day)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.data?.map((period) => (
                  <tr key={period.id}>
                    <th className="border-b border-line px-2 py-2 text-left align-top">
                      <span className="block text-sm font-medium text-ink">{period.name}</span>
                      <span className="block font-mono text-xs text-subtle">
                        {period.startTime.slice(0, 5)}–{period.endTime.slice(0, 5)}
                      </span>
                    </th>

                    {DAYS.map((day) => {
                      const entry = entryAt(day, period.id);

                      // A non-teachable period is greyed rather than made
                      // clickable: the backend refuses lessons there, and an
                      // affordance that always fails is worse than none.
                      if (!period.teachable) {
                        return (
                          <td
                            key={day}
                            className="border-b border-line bg-surface-2/50 px-2 py-2 text-center text-xs text-subtle"
                          >
                            —
                          </td>
                        );
                      }

                      return (
                        <td key={day} className="border-b border-line px-1 py-1 align-top">
                          {entry === undefined ? (
                            editable ? (
                              <button
                                type="button"
                                onClick={() => setSlot({ day, period })}
                                className="flex h-full w-full items-center justify-center rounded-md border border-dashed border-line py-3 text-subtle transition-colors hover:border-accent hover:text-accent"
                                aria-label={`Add a lesson on ${dayLabel(day)} at ${period.name}`}
                              >
                                <Plus className="h-4 w-4" aria-hidden="true" />
                              </button>
                            ) : (
                              <span className="block py-3 text-center text-xs text-subtle">Free</span>
                            )
                          ) : (
                            <div className="group relative rounded-md bg-accent-soft px-2 py-2">
                              <p className="text-xs font-medium text-accent-soft-fg">
                                {entry.subjectName ?? 'Lesson'}
                              </p>
                              <p className="mt-0.5 text-[11px] text-muted">{entry.teacherName}</p>
                              {entry.room !== undefined && (
                                <p className="text-[11px] text-subtle">{entry.room}</p>
                              )}
                              {editable && (
                                <button
                                  type="button"
                                  onClick={() => removeEntry.mutate(entry.id)}
                                  className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100"
                                  aria-label="Remove this lesson"
                                >
                                  <X className="h-3.5 w-3.5 text-muted hover:text-danger" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New draft timetable"
        description="Drafts are not the schedule anyone follows until published."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)} disabled={createTimetable.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => createTimetable.mutate()}
              loading={createTimetable.isPending}
              disabled={effectiveFrom === ''}
            >
              Create draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="e.g. Spring term"
          />
          <TextField
            label="Effective from"
            type="date"
            required
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={slot !== null}
        onClose={() => setSlot(null)}
        title={slot === null ? 'Add a lesson' : `${dayLabel(slot.day)} · ${slot.period.name}`}
        description="The teacher comes from the assignment, so the lesson cannot name someone who does not teach this subject to this class."
        footer={
          <>
            <Button variant="ghost" onClick={() => setSlot(null)} disabled={addEntry.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => addEntry.mutate()}
              loading={addEntry.isPending}
              disabled={assignmentId === ''}
            >
              Add lesson
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Subject and teacher"
            required
            value={assignmentId}
            onChange={(event) => setAssignmentId(event.target.value)}
            hint={
              assignments.data?.length === 0
                ? 'No teachers are assigned to this class yet. Assign one first.'
                : undefined
            }
          >
            <option value="">Select…</option>
            {assignments.data
              ?.filter((assignment) => assignment.status === 'ACTIVE')
              .map((assignment) => (
                <option key={assignment.id} value={assignment.id}>
                  {assignment.subjectName} · {assignment.teacherName}
                </option>
              ))}
          </SelectField>

          <TextField
            label="Room"
            value={room}
            onChange={(event) => setRoom(event.target.value)}
            placeholder="e.g. Lab 2"
          />
        </div>
      </Modal>
    </div>
  );
}
